import { useState, useEffect } from 'react';

/**
 * Canonical breakpoints — matches Clip Factory and shell.
 *
 *   ≤ 480px   →  mobile   (phones in portrait)
 *   481–1194px →  tablet   (tablets incl. iPad Pro 11" landscape)
 *   > 1194px  →  desktop  (standard+ viewports)
 */

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const MOBILE_MAX = 480;
const TABLET_MAX = 1194;

export const BREAKPOINTS = {
  mobile: MOBILE_MAX,
  tablet: TABLET_MAX,
} as const;

function resolve(): Breakpoint {
  const w = window.innerWidth;
  if (w <= MOBILE_MAX) return 'mobile';
  if (w <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(resolve);

  useEffect(() => {
    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const tabletQuery = window.matchMedia(`(max-width: ${TABLET_MAX}px)`);

    const update = () => setBp(resolve());

    mobileQuery.addEventListener('change', update);
    tabletQuery.addEventListener('change', update);

    return () => {
      mobileQuery.removeEventListener('change', update);
      tabletQuery.removeEventListener('change', update);
    };
  }, []);

  return bp;
}
